/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || | 
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2023 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Chakra imports
import {
  Box,
  HStack,
  Select,
  SimpleGrid,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import CheckTable from "views/admin/chipstats/components/CheckTable";
import {
  columnsDataCheck,
} from "views/admin/chipstats/variables/columnsData";

export default function UserReports() {
  // Chakra Color Mode
  const authToken = localStorage.getItem("authToken");
  const userData = JSON.parse(localStorage.getItem('userData'));

  const [alert, setAlert] = useState(['', '', ''])
  const [chips, setChips] = useState([])

  const [departments, setDepartments] = useState([]);
  const [departmentID, setDepartmentID] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [organizationID, setOrganizationID] = useState('');

  useEffect(() => {
    if (!userData.is_super_user) {
      setOrganizationID(userData.organization?.$oid);
    }
  }, [userData]);

  const toast = useToast();

  useEffect(() => {
    if (alert[0] !== '') {
      toast({
        title: alert[1],
        description: alert[2],
        status: alert[0],
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  }, [alert]);

  // Fetch organizations
  const fetchOrganizations = async () => {
    let url;
    if (userData.is_super_user) {
      url = `https://api.beehiveproject.no/organization`;
    } else {
      url = `https://api.beehiveproject.no/organization/${userData.organization.$oid}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        }
      });

      let data = await response.json();
      if (!userData.is_super_user) {
        data = Array.isArray(data) ? data : [data];
      }
      setOrganizations(data);
      return data;
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchOrganizations();
    }
  }, [authToken]);
  
  // Fetch departments
  const fetchDepartments = async () => {
    let url;
    if (!organizationID) {
      url = `https://api.beehiveproject.no/department`;
    }
    else {
      url = `https://api.beehiveproject.no/organization/${organizationID}/departments`;
    }
    try {
      const response = await fetch(url,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const data = await response.json();
      setDepartments(data);

      return data;
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [organizationID]);

  // Fetch chips
  const fetchChips = async () => {
    const [latestOrganizations, latestDepartments] = await Promise.all([
      fetchOrganizations(),
      fetchDepartments(),
    ]);

    try {
      let url;
      if (organizationID === '') {
        url = `https://api.beehiveproject.no/chip`;
      } else {
        url = `https://api.beehiveproject.no/organization/${organizationID}/chips`;
      }
      if (departmentID) {
        url += `?department=${departmentID}`;
      }
      const chipResponse = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const chips = await chipResponse.json();

      const data = chips
        .map((chip) => {
          
          const latestDepartment = latestDepartments.find(
            (dep) => dep._id.$oid === chip.department?.$oid
          );

          const latestOrganization = latestOrganizations.find(
            (org) => org._id.$oid === chip.organization?.$oid
          );
          
          return {
            ...chip,
            department: {
              $oid: latestDepartment?._id.$oid,
              name: latestDepartment?.name || "Ukjent avdeling",
            },
            organization: {
              $oid: latestOrganization?._id.$oid,
              name: latestOrganization?.name || "Ukjent organisasjon",
            },
          };
        });
      
      setChips(data);
      console.log("chipdata", data);
    } catch (error) {
      console.error("Failed to fetch chips:", error);
    }
  };

  useEffect(() => {
    fetchChips();
  }, [organizationID, departmentID]);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <HStack spacing="20px" mb="20px">
        { userData['is_super_user'] && (
        <Select marginBottom={5} variant="filled" defaultValue={''}
          onChange={async (event) => {
            const value = event.target.value;

            if (value === "all") {
              setOrganizationID('');
              setDepartmentID('');
            } else {
              const data = JSON.parse(value);
              const newOrgID = data._id.$oid;
              setOrganizationID(newOrgID);
              setDepartmentID('');
            }
          }}
        >
          <option value="all">Alle organisasjoner</option>
          {organizations.map((org) => <option value={JSON.stringify(org)} key={org._id.$oid}>{org.name}</option>)}
        </Select>
        )}
        <Select marginBottom={5} variant="filled" defaultValue={''}
          onChange={async (event) => {
            const value = event.target.value;

            if (value === "all") {
              setDepartmentID('');
            } else {
              const data = JSON.parse(value);
              const newDepID = data._id.$oid;
              setDepartmentID(newDepID);
            }
          }}
        >
          <option value="all">Alle avdelinger</option>
          {departments.map((dep) => <option value={JSON.stringify(dep)} key={dep._id.$oid}>{dep.name}</option>)}
        </Select>
      </HStack>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 1 }} gap='20px' mb='20px'>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 1, xl: 1 }} gap='20px' mb='20px'>
        <CheckTable columnsData={columnsDataCheck} tableData={chips} setTableData={setChips} departmentsData={departments} setAlert={setAlert} organizationsData={organizations}/>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
      </SimpleGrid>
    </Box>
  );
}
